import { TranslatePipe } from '../../filters/translate.pipe';
import { ElementRef } from '@angular/core';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import CallStartService from '../../services/call-start.service';
import ChatService from '../../services/chat.service';
import ChatRestServices from '../../services/chat-rest.service';
import Config from '../../utils/config.service';
import Toast from '../../services/toast.service';
import Session from '../../utils/session.service';
import DialogServices from '../../services/dialog.service';
import AngularHelperService from '../../services/angular-helper.service';
import AngularFilterHelperService from '../../services/angular-filter-helper.service';
import AngularBroadcastHelperService from '../../services/angular-broadcast-helper.service';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import moment from 'moment';
import { Input, Output, EventEmitter } from '@angular/core';
import { InputDatepickerNewComponent } from '../input-datepicker-new/input-datepicker-new.component';
import { ModalMedicationSearchComponent } from '../modal-medication-search/modal-medication-search.component';
import { ModalChatDownloadComponent } from '../modal-chat-download/modal-chat-download.component';
import { ModalQuestionnaireSearchComponent } from '../modal-questionnaire-search/modal-questionnaire-search.component';
import { ModalServiceProviderSearchComponent } from '../modal-service-provider-search/modal-service-provider-search.component';
import { ModalChatUploadTypePickerComponent } from '../modal-chat-upload-type-picker/modal-chat-upload-type-picker.component';
import { Subscription } from 'rxjs';

@Component({
	standalone: true,
	selector: 'input-messenger',
	templateUrl: './input-messenger.component.html',
	imports: [ CommonModule, TranslatePipe, FormsModule, InputDatepickerNewComponent ],
	styles: [`
		input[type="date"]::-webkit-calendar-picker-indicator,
		input[type="time"]::-webkit-calendar-picker-indicator {
			cursor: pointer;
		}
	`]
})
export class InputMessengerComponent implements OnInit, OnDestroy {
	private callStartService: CallStartService = inject(CallStartService);
	private chatService: ChatService = inject(ChatService);
	private chatRestServices: ChatRestServices = inject(ChatRestServices);
	private config: Config = inject(Config);
	private toast: Toast = inject(Toast);
	private session: Session = inject(Session);
	private dialogServices: DialogServices = inject(DialogServices);
	private angularHelperService: AngularHelperService = inject(AngularHelperService);
	private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);
	private angularBroadcastHelperService: AngularBroadcastHelperService = inject(AngularBroadcastHelperService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	
	@Input() isPopup: any | undefined;
	@Input() chat: any;
	@Input() group: any;
	@Output() chatChange: EventEmitter<any> = new EventEmitter<any>(); // TODO ANGULAR_2: fix calling event emitter
	
	vm: any = undefined;
	broadcastSubscriptions: Array<Subscription> = [];
	
	constructor(private elementRef: ElementRef) {
		this.vm = this;
	}
	
	ngOnInit() {
		this.vm.input = this.getTextInput();
		this.vm.imageId = 'img-' + Date.now();
		this.vm.videoId = 'vid-' + Date.now();
		this.vm.documentId = 'doc-' + Date.now();
		this.vm.uploadPickerId = 'pick-' + Date.now();
		this.vm.reservationTimeOptions = this.createReservationTimeOptions();
		this.vm.user = this.session.getSession().user;
		this.vm.isDoctor = this.vm.user.businessUserType === 'SERVICE_PROVIDER';
		this.vm.canCreateBookingOffer = this.vm.isDoctor && this.vm.user.partnerAccount === true;
		this.vm.isMentalHealthServiceProvider = this.vm.user && this.vm.user.service && this.vm.user.service.mentalHealthServiceProvider;
		this.vm.canAccessMentalHealthQuestionnaire = this.vm.isDoctor
			&& (this.vm.user.service.subcategory === 'COACHING' || this.vm.user.service.subcategory === 'PSYCHOLOGY');
		this.vm.canRecommendMedications = this.config.getPharmacyConfig().MEDICATION_SEARCH_SUPPORTED
			&& this.vm.isDoctor
			&& (this.vm.user.service.subcategory === 'GENERAL_MEDICAL_PRACTICE' || this.vm.user.service.subcategory === 'PHARMACIST');
		
		this.broadcastSubscriptions.push(
			this.angularBroadcastHelperService.onEvent(
				this, this.config.getBroadcastConfig().Chat.FOCUS_INPUT,
				() => {
					this.handleInputFocus();
				})
		);
		
		this.angularBroadcastHelperService.onRootEvent(this.config.getBroadcastConfig().Chat.UPDATE_CHAT_STATUS, (event, chatEvent, chatId) => {
			if (this.chat.id === chatId) {
				if (chatEvent.eventType === 'CHATBOT_DISABLED') {
					this.chat.activeAiSessionId = null;
					this.chat.aiStatus = null;
					this.vm.aiChatActive = false;
				} else if (chatEvent.eventType === 'CHATBOT_ACTIVATED') {
					this.chat.activeAiSessionId = true;
					this.vm.aiChatActive = true;
				} else if (chatEvent.eventType === 'INTERVENTION_NEEDED') {
					this.chat.activeAiSessionId = null;
					this.chat.aiStatus = 'INTERVENTION_REQUIRED';
					this.vm.aiChatActive = false;
				} else if (chatEvent.eventType === 'CRITICAL_INTERVENTION_NEEDED') {
					this.chat.activeAiSessionId = null;
					this.chat.aiStatus = 'CRITICAL_INTERVENTION_REQUIRED';
					this.vm.aiChatActive = false;
				} else if (chatEvent.eventType === 'CHAT_ENABLED') {
					this.chat.disabled = false;
				} else if (chatEvent.eventType === 'CHAT_DISABLED') {
					this.chat.disabled = true;
				} else if (chatEvent.eventType === 'CHATBOT_SUMMARY') {
					this.chat.aiStatus = 'SUMMARY_FORWARD_REQUIRED';
				}
			}
		});
		
		this.vm.handleKeypress = this.handleKeypress;
		this.vm.handleKeyup = this.handleKeyup;
		this.vm.handleMediaUpload = this.handleMediaUpload;
		this.vm.openMedicationSearch = this.openMedicationSearch;
		this.vm.openQuestionnaireSearch = this.openQuestionnaireSearch;
		this.vm.openServiceProviderSearch = this.openServiceProviderSearch;
		this.vm.openUploadFileTypePicker = this.openUploadFileTypePicker;
		this.vm.openFileSelect = this.openFileSelect;
		this.vm.sendTextMessage = this.sendTextMessage;
		this.vm.openBookingReservationMode = this.openBookingReservationMode;
		this.vm.cancelBookingReservationMode = this.cancelBookingReservationMode;
		this.vm.sendBookingReservation = this.sendBookingReservation;
		this.vm.openNativePicker = this.openNativePicker;
		this.vm.cancelEdit = this.cancelEdit;
		this.vm.call = this.call;
		this.vm.isPreVisitChat = this.angularRoutingHelperService.getCurrentPath() === 'pre-visit-chat/:id';
		this.vm.reserveBookingMode = false;
		this.vm.reservationDate = moment().add(1, 'hour').format('YYYY-MM-DD');
		this.vm.reservationTime = moment().add(1, 'hour').format('HH:mm');
		
		if (this.chat.activeAiSessionId) {
			this.vm.aiChatActive = true;
		}
		
		var currentChat = undefined;
		this.vm.chatChangeListener = this.angularHelperService.createInterval(() => {
			if (this.chat && currentChat !== this.chat.id) {
				currentChat = this.chat.id;
				if (this.chat.activeAiSessionId) {
					this.vm.aiChatActive = true;
				} else {
					this.vm.aiChatActive = false;
				}
			}
		}, 200);
	}
	
	ngOnDestroy() {
		for (var b = 0; b < this.broadcastSubscriptions.length; b++) {
			this.broadcastSubscriptions[b].unsubscribe();
		}
		if (this.vm.chatChangeListener) {
			this.angularHelperService.cancelInterval(this.vm.chatChangeListener);
		}
	}
	
	onFileSelected(event: Event, type: string): void {
		const input = event.target as HTMLInputElement;
		this.handleMediaUpload(input, type);
	}
	
	handleMediaUpload(input, type) {
		var chat: any = this.angularHelperService.copy(this.vm.chat);
		var medias: any = input.files;
		if (!medias || !medias.length || this.vm.processing) return;
		if (type !== 'video' && type !== 'picture' && type !== 'document') return;
		var media: any = medias[0];
		media.groupId = Date.now();
		var data: any = new FormData();
		data.append('file', media);
		
		this.vm.processing = true;
		this.angularBroadcastHelperService.rootBroadcast(this.config.getBroadcastConfig().Chat.MEDIA_UPLOAD_INIT,
			{ mediaType: type, media: media, chat: chat.id, user: this.vm.user });
		
		var params: any = {
			id: chat && chat.id || chat.privateChatFriend.id,
			fileName: media.name,
		};
		
		var successCallback = (res) => {
			res.groupId = media.groupId;
			if (!this.vm.chat.id) {
				this.initNewChat(this.vm.chat.privateChatFriend, this.createMediaData(type, res, chat));
			} else {
				this.angularBroadcastHelperService.rootBroadcast(
					this.config.getBroadcastConfig().Chat.MEDIA_UPLOAD_START,
					this.createMediaData(type, res, chat)
				);
			}
			this.handleInputReset(input);
		};
		var errorCallback = (err) => {
			this.angularBroadcastHelperService.rootBroadcast(
				this.config.getBroadcastConfig().Chat.MEDIA_UPLOAD_ERROR,
				this.createMediaData(type, media, chat, err)
			);
			this.handleInputReset(input);
		};
		
		switch (type) {
			case 'video':
				if (chat && chat.id) {
					this.chatRestServices.uploadVideoToChat(params, data, successCallback, errorCallback);
				} else {
					this.chatRestServices.uploadVideoToFriend(params, data, successCallback, errorCallback);
				}
				break;
			case 'picture':
				if (chat && chat.id) {
					this.chatRestServices.uploadImageToChat(params, data, successCallback, errorCallback);
				} else {
					this.chatRestServices.uploadImageToFriend(params, data, successCallback, errorCallback);
				}
				break;
			case 'document':
				if (chat && chat.id) {
					this.chatRestServices.uploadDocumentToChat(params, data, successCallback, errorCallback);
				} else {
					this.chatRestServices.uploadDocumentToFriend(params, data, successCallback, errorCallback);
				}
				break;
		}
	}
	
	createMediaData(type, media, chat, error?) {
		var data: any = media;
		data.chat = chat;
		data.user = this.vm.user;
		data.modified = new Date();
		data.created = new Date();
		data.postType = type.toUpperCase();
		data.contentType = type.toUpperCase();
		if (error) data.error = error;
		
		return data;
	}
	
	handleInputReset(input) {
		this.vm.processing = false;
		input.value = '';
	}
	
	handleKeyup(e) {
		if (!e || this.vm.loading) return;
		var key: any = e.which || e.keyCode;
		if ((key === 8 || key === 46) && this.vm.typing) {
			if (!this.chat.message || this.chat.message.length == 0) {
				this.vm.typing = false;
				this.chatRestServices.sendChatEvent({ chat: { id: this.vm.chat.id }, eventType: 'TYPING_END' }, (res) => {
					console.log("Success sending chat event");
				});
			}
		}
	}

	handleKeypress(e) {
		if (!e || this.vm.loading) return;
		var key: any = e.which || e.keyCode;
		if (this.chat.message && this.chat.message.length > 0) {
			if (!this.vm.typing) {
				this.vm.typing = true;
				this.chatRestServices.sendChatEvent({ chat: { id: this.vm.chat.id }, eventType: 'TYPING_START' }, (res) => {
					console.log("Success sending chat event");
				});
			}
		}
		if (key !== 13) return;
		if (e.preventDefault) e.preventDefault();
		if (this.vm.chat.editingMessage) {
			this.updateTextMessage();
		} else {
			this.sendTextMessage();
		}
	}

	handleInputFocus() {
		this.angularHelperService.createTimer(() => { this.focusTextInput(); });
	}

	getTextInput() {
		return this.angularHelperService.element(this.elementRef.nativeElement)[0].querySelector('textarea.c-form__input');
	}

	focusTextInput() {
		this.vm.input = this.getTextInput();
		if (this.vm.input && this.vm.input.focus) {
			this.vm.input.focus();
		}
	}

	blurTextInput() {
		this.vm.input = this.getTextInput();
		if (this.vm.input && this.vm.input.blur) {
			this.vm.input.blur();
		}
	}
	
	call() {
		if (!this.vm.isDoctor) return;
		this.vm.callInitInProgress = true;
		this.callStartService.startBusinessCallForConsultant(this.vm.chat.privateChatFriend.id);
		this.angularHelperService.createTimer(() => { this.vm.callInitInProgress = false; }, 500);
	}
	
	downloadChat() {
		this.dialogServices.show({
			controller: ModalChatDownloadComponent,
			chat: this.vm.chat,
			clickOutsideToClose: false,
			escapeToClose: false,
			multiple: false,
		});
	}
	
	openMedicationSearch() {
		this.dialogServices.show({
			controller: ModalMedicationSearchComponent,
			chat: this.vm.chat,
			clickOutsideToClose: false,
			escapeToClose: false,
			multiple: false,
		});
	}
	
	openQuestionnaireSearch() {
		this.dialogServices.show({
			controller: ModalQuestionnaireSearchComponent,
			chat: this.vm.chat ,
			clickOutsideToClose: false,
			escapeToClose: false,
			multiple: false,
		});
	}
	
	openServiceProviderSearch() {
		this.dialogServices.show({
			controller: ModalServiceProviderSearchComponent,
			chat: this.vm.chat ,
			clickOutsideToClose: false,
			escapeToClose: false,
			multiple: false,
		});
	}
	
	openUploadFileTypePicker() {
		this.dialogServices.show({
			controller: ModalChatUploadTypePickerComponent,
			chat: this.vm.chat ,
			clickOutsideToClose: false,
			escapeToClose: false,
			multiple: false,
		});
	}

	openBookingReservationMode() {
		this.vm.reserveBookingMode = true;
		this.vm.chat.message = undefined;
		this.vm.typing = false;
		this.vm.reservationDateModel = undefined;
		this.vm.reservationTime = undefined;
		this.vm.reservationMinDate = moment().format();
	}

	openNativePicker(event: any) {
		const input = event?.target as HTMLInputElement;
		if (input && typeof (input as any).showPicker === 'function') {
			(input as any).showPicker();
		}
	}

	cancelBookingReservationMode() {
		this.vm.reserveBookingMode = false;
	}
	
	openFileSelect(id) {
		var el: any = document.getElementById(id);
		if (el) {
			el.click();
		}
	}

	updateTextMessage() {
		if (!this.chat.message || !this.chat.message.trim()) return;
		this.blurTextInput();
		this.vm.loading = true;
		var chat: any = this.angularHelperService.copy(this.vm.chat);
		var message: any = {
			id: this.chat.editingMessage?.id,
			contentType: 'TEXT',
			content: this.angularHelperService.copy(this.chat.message).trim(),
			chat: {
				id: this.vm.chat.id
			}
		};
		if (!message.content || !message.content.length) return;


		this.chatRestServices.updateChatMessage(
				{ forUser: chat.privateChatFriend.id, isEdited: true}, message,
				(res) => {
					this.chat.editingMessage = null;
					this.chat.message = undefined;
					this.vm.loading = false;
					this.vm.typing = false;
					this.focusTextInput();
					this.angularBroadcastHelperService.rootBroadcast(this.config.getBroadcastConfig().Chat.CHAT_POST_EDITED, [res], chat.id);
				},
				(err) => {
					console.error(err);
					this.vm.loading = false;
				}
			);

	}

	sendTextMessage() {
		if (!this.chat.message || !this.chat.message.trim()) return;
		this.blurTextInput();
		this.vm.loading = true;
		
		var chat: any = this.angularHelperService.copy(this.vm.chat);
		var message: any = {
			contentType: 'TEXT',
			content: this.angularHelperService.copy(this.chat.message).trim(),
			chat: {
				id: this.vm.chat.id
			}
		};
		if (!message.content || !message.content.length) return;

		this.chatRestServices.sendTextMessage({ forUser: chat.privateChatFriend.id }, message,
			(res) => {
				if (!this.vm.chat.id) {
					this.initNewChat(this.vm.chat.privateChatFriend, res);
				} else {
					this.angularBroadcastHelperService.rootBroadcast(this.config.getBroadcastConfig().Chat.NEW_MESSAGE, [res], chat.id);
				}
				this.chat.message = undefined;
				this.vm.loading = false;
				this.vm.typing = false;
				this.focusTextInput();
			},
			(err) => {
				var message: any = err.error && err.error[0] && err.error[0].message;
				switch (message) {
					case 'MaxLength':
						this.toast.showAsync(this.angularFilterHelperService.translate('Chats.Errors.Too_Long'), null, null, 'error');
						break;
				}
				this.vm.loading = false;
			});
	}

	sendBookingReservation() {
		if (!this.vm.reservationDateModel || !this.vm.reservationTime || this.vm.loading) {
			return;
		}

		const scheduledStart = moment(this.vm.reservationDateModel).hour(Number(this.vm.reservationTime.split(':')[0])).minute(Number(this.vm.reservationTime.split(':')[1])).second(0).millisecond(0);
		if (!scheduledStart.isValid()) {
			this.toast.showAsync(this.angularFilterHelperService.translate('Common.Failed'), null, null, 'error');
			return;
		}

		this.vm.loading = true;
		const chat: any = this.angularHelperService.copy(this.vm.chat);
		const booking: any = {
			scheduledStart: scheduledStart.toISOString(),
			platform: this.config.getSettings().PLATFORM
		};

		this.chatRestServices.createBookingOfferFromChat({ forUser: chat.privateChatFriend.id }, booking,
			(res) => {
				if (!this.vm.chat.id) {
					this.initNewChat(this.vm.chat.privateChatFriend, res);
				} else {
					this.angularBroadcastHelperService.rootBroadcast(this.config.getBroadcastConfig().Chat.NEW_MESSAGE, [res], chat.id);
				}
				this.vm.reserveBookingMode = false;
				this.vm.reservationDateModel = undefined;
				this.vm.reservationTime = undefined;
				this.vm.loading = false;
				this.vm.typing = false;
			},
			(err) => {
				const errorMessage: any = err?.error?.[0]?.message || err?.error?.errorCode;
				if (errorMessage === 'Overlapping') {
					this.toast.showAsync(this.angularFilterHelperService.translate('Booking.Errors.Overlapping_Time'), null, 6000, 'warning');
				} else {
					this.toast.showAsync(this.angularFilterHelperService.translate('Common.Failed'), null, 6000, 'error');
				}
				this.vm.loading = false;
			});
	}

	createReservationTimeOptions() {
		var options: Array<string> = [];
		for (var hour = 0; hour < 24; hour++) {
			for (var minute = 0; minute < 60; minute += 15) {
				options.push(('0' + hour).slice(-2) + ':' + ('0' + minute).slice(-2));
			}
		}
		return options;
	}

	getBookingReservationInputStyle() {
		return {
			'display': 'flex',
			'flex-direction': 'column',
			'gap': '10px',
			'padding': '12px',
			'background': '#f6f7f9',
			'border-radius': '6px',
			'margin-bottom': '10px'
		};
	}

	getBookingReservationRowStyle() {
		return {
			'display': 'grid',
			'grid-template-columns': '1fr 1fr',
			'gap': '10px'
		};
	}

	cancelEdit() {
		this.vm.chat.editingMessage = null;
		this.vm.chat.message = '';
	}
	
	initNewChat(user, message) {
		this.chatRestServices.getChatByFriendId({ friendUserId: user.id },
			(chat) => {
				chat.privateChatFriend = user;
				if (!chat.privateChatFriend.fullName && user.firstname && user.lastname) {
					chat.privateChatFriend.fullName = user.firstname + ' ' + user.lastname;
				}
				chat.lastPost = message;
				chat.lastSeenPostId = message.id;
				chat.conversation = this.vm.chat.conversation;
				chat.localId = this.vm.chat.localId;
				this.chatService.initNewChat(chat);
				this.vm.chat = chat;
				message.chat = chat;
				this.angularHelperService.createTimer(() => {
					switch (message.postType) {
						case 'VIDEO':
						case 'PICTURE':
							this.angularBroadcastHelperService.rootBroadcast(
								this.config.getBroadcastConfig().Chat.MEDIA_UPLOAD_START,
								message
							);
							break;
						default:
							this.angularBroadcastHelperService.rootBroadcast(
								this.config.getBroadcastConfig().Chat.NEW_MESSAGE,
								[message],
								chat.id
							);
					}
				});
			}, console.error);
	}
	
}
